<template>
  <Teleport to="body">
    <div class="feedback" :class="{ 'feedback--picking': picking }">
      <div v-if="highlight && picking" class="feedback__highlight" :style="highlightStyle" aria-hidden="true" />

      <div v-if="picking" class="feedback__hint" role="status">
        <mdicon name="cursor-default-click-outline" :size="20" aria-hidden="true" />
        <span>Click anywhere to leave a comment</span>
        <button type="button" @click="cancelPicking">Cancel <kbd>Esc</kbd></button>
      </div>

      <template v-if="showPins">
        <button
          v-for="pin in pins"
          :key="pin.thread.id"
          type="button"
          class="feedback__pin"
          :class="{
            'feedback__pin--selected': selectedId === pin.thread.id,
            'feedback__pin--resolved': pin.thread.status === 'resolved',
          }"
          :style="{ left: `${pin.x}px`, top: `${pin.y}px` }"
          :aria-label="`Comment ${pin.thread.number} by ${pin.thread.author.name}: ${pin.thread.body}`"
          :title="`${pin.thread.author.name}: ${pin.thread.body}`"
          @click="selectThread(pin.thread)"
        >
          {{ pin.thread.number }}
        </button>
      </template>

      <aside v-if="panelOpen" class="feedback__panel" aria-label="Page feedback">
        <header class="feedback__header">
          <div>
            <span class="feedback__eyebrow">PAGE FEEDBACK</span>
            <h2>
              Feedback <span>{{ openCount }}</span>
            </h2>
          </div>
          <button class="feedback__icon" type="button" aria-label="Close feedback" @click="closePanel">
            <mdicon name="close" :size="20" aria-hidden="true" />
          </button>
        </header>
        <div class="feedback__identity">
          <span class="feedback__avatar">{{ initials(currentName) }}</span><span>Commenting as <strong>{{ currentName }}</strong></span>
        </div>

        <div v-if="error" class="feedback__error" role="alert">
          {{ error }} <button v-if="!draftContext && !selectedThread" type="button" @click="refresh">Retry</button>
        </div>

        <div class="feedback__content">
          <form v-if="draftContext" class="feedback__composer" @submit.prevent="postComment">
            <div class="feedback__section-head">
              <h3>New comment</h3>
              <span class="feedback__badge">Pinned</span>
            </div>
            <div class="feedback__context">
              <mdicon name="map-marker-outline" :size="18" aria-hidden="true" />
              <div>
                <strong>{{ draftContext.title }}</strong>
                <p>{{ draftContext.text || draftContext.tag }}</p>
              </div>
            </div>
            <label for="feedback-comment">What should we change?</label>
            <textarea
              id="feedback-comment"
              ref="commentInput"
              v-model="draft"
              rows="5"
              maxlength="4000"
              placeholder="Describe what you expected or what could be better…"
              @keydown.meta.enter.prevent="postComment"
              @keydown.ctrl.enter.prevent="postComment"
            />
            <p class="feedback__muted">The page, element, position and your name are included.</p>
            <div class="feedback__actions">
              <button class="feedback__secondary" type="button" :disabled="busy" @click="discardDraft">Cancel</button><button class="feedback__primary" type="submit" :disabled="busy || !draft.trim()">
                {{ busy ? 'Saving…' : 'Post comment' }}<mdicon name="arrow-up" :size="17" aria-hidden="true" />
              </button>
            </div>
          </form>

          <section v-else-if="selectedThread" class="feedback__thread">
            <div class="feedback__section-head">
              <button class="feedback__back" type="button" @click="backToList">
                <mdicon name="arrow-left" :size="18" aria-hidden="true" /> All comments
              </button><span class="feedback__badge">#{{ selectedThread.number }} · {{ selectedThread.status }}</span>
            </div>
            <div class="feedback__message">
              <span class="feedback__avatar">{{ initials(selectedThread.author.name) }}</span>
              <div>
                <strong>{{ selectedThread.author.name }}</strong><time :datetime="selectedThread.createdAt">{{ formatTime(selectedThread.createdAt) }}</time>
                <p>{{ selectedThread.body }}</p>
              </div>
            </div>
            <button class="feedback__context feedback__context--button" type="button" @click="goToLocation">
              <mdicon name="map-marker-outline" :size="20" aria-hidden="true" /><span><strong>{{ selectedThread.context.title }}</strong><span>{{ selectedThread.context.text || selectedThread.context.tag }}</span><small>Go to location <mdicon name="arrow-top-right" :size="14" aria-hidden="true" /></small></span>
            </button>
            <details class="feedback__details">
              <summary>Captured context</summary>
              <dl>
                <dt>Page</dt>
                <dd>{{ selectedThread.context.path }}</dd>
                <dt>Section</dt>
                <dd>{{ selectedThread.context.heading || 'Page' }}</dd>
                <dt>Element</dt>
                <dd>
                  <code>{{ selectedThread.context.selector }}</code>
                </dd>
                <dt>Viewport</dt>
                <dd>{{ selectedThread.context.viewport.width }} × {{ selectedThread.context.viewport.height }}</dd>
              </dl>
            </details>
            <div v-for="reply in selectedThread.replies" :key="reply.id" class="feedback__message">
              <span class="feedback__avatar feedback__avatar--reply">{{ initials(reply.author.name) }}</span>
              <div>
                <strong>{{ reply.author.name }}</strong><time :datetime="reply.createdAt">{{ formatTime(reply.createdAt) }}</time>
                <p>{{ reply.body }}</p>
              </div>
            </div>
            <p v-if="selectedThread.resolvedBy" class="feedback__resolved">
              <mdicon name="check-circle-outline" :size="17" aria-hidden="true" /> Resolved by
              {{ selectedThread.resolvedBy.name }}
            </p>
            <form class="feedback__reply" @submit.prevent="postReply">
              <label for="feedback-reply">Reply to this comment</label><textarea id="feedback-reply" v-model="replyText" rows="3" maxlength="4000" placeholder="Add a reply…" />
              <div class="feedback__actions">
                <button class="feedback__secondary" type="button" :disabled="busy" @click="toggleResolved">
                  <mdicon
                    :name="selectedThread.status === 'open' ? 'check' : 'restore'"
                    :size="17"
                    aria-hidden="true"
                  />{{ selectedThread.status === 'open' ? 'Resolve' : 'Reopen' }}
                </button><button class="feedback__primary" type="submit" :disabled="busy || !replyText.trim()">
                  {{ busy ? 'Saving…' : 'Reply' }}
                </button>
              </div>
            </form>
            <button v-if="canDelete" class="feedback__delete" type="button" :disabled="busy" @click="deleteComment">
              <mdicon name="trash-can-outline" :size="17" aria-hidden="true" /> Delete comment
            </button>
          </section>

          <template v-else>
            <button class="feedback__primary feedback__add" type="button" @click="startPicking">
              <mdicon name="plus" :size="18" aria-hidden="true" /> Add comment <kbd>C</kbd>
            </button>
            <div class="feedback__filters">
              <label class="feedback__sr-only" for="feedback-status">Comment status</label><select id="feedback-status" v-model="statusFilter">
                <option value="open">Open ({{ openCount }})</option>
                <option value="resolved">Resolved</option>
                <option value="all">All comments</option>
              </select><label><input v-model="onlyThisPage" type="checkbox" /> This page</label>
            </div>
            <p v-if="loading" class="feedback__muted">Loading feedback…</p>
            <div v-else-if="!filteredThreads.length" class="feedback__empty">
              <div class="feedback__empty-icon">
                <mdicon name="comment-text-outline" :size="28" aria-hidden="true" />
              </div>
              <h3>{{ threads.length ? 'No matching comments' : 'Point. Comment. Improve.' }}</h3>
              <p>
                {{
                  threads.length
                    ? 'Try another filter or leave a new comment.'
                    : 'Click Add comment, then point to anything on the page. Your feedback stays attached to that spot.'
                }}
              </p>
            </div>
            <button
              v-for="thread in filteredThreads"
              :key="thread.id"
              type="button"
              class="feedback__card"
              @click="selectThread(thread)"
            >
              <span class="feedback__card-head"><span class="feedback__avatar">{{ initials(thread.author.name) }}</span><span><strong>{{ thread.author.name }}</strong><time :datetime="thread.createdAt">{{ formatTime(thread.createdAt) }}</time></span><span class="feedback__number">#{{ thread.number }}</span></span><span class="feedback__card-body">{{ thread.body }}</span><span class="feedback__card-foot"><span>{{ thread.context.title }}</span><span v-if="thread.replies.length"><mdicon name="comment-outline" :size="14" aria-hidden="true" /> {{ thread.replies.length }}</span><mdicon
                v-if="thread.status === 'resolved'"
                name="check-circle-outline"
                :size="17"
                aria-label="Resolved"
              /></span>
            </button>
          </template>
        </div>

        <footer class="feedback__footer">
          <div>
            <button type="button" :aria-pressed="showPins" @click="togglePins">
              <mdicon :name="showPins ? 'eye-outline' : 'eye-off-outline'" :size="18" aria-hidden="true" />{{
                showPins ? 'Hide pins' : 'Show pins'
              }}
            </button><button type="button" :disabled="loading" @click="refresh">
              <mdicon name="refresh" :size="18" aria-hidden="true" /> Refresh
            </button>
          </div>
        </footer>
      </aside>

      <button
        ref="launcher"
        class="feedback__launcher"
        type="button"
        :aria-expanded="panelOpen"
        :aria-pressed="picking"
        @click="togglePanel"
      >
        <mdicon name="comment-outline" :size="21" aria-hidden="true" /><span>Feedback</span><span v-if="openCount" class="feedback__count">{{ openCount }}</span>
      </button>
      <div v-if="notice" class="feedback__notice" role="status">{{ notice }}</div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import type { FeedbackHighlight, FeedbackPin } from '@/types/Components/Feedback'
import type { FeedbackContext, FeedbackThread } from '@/types/Models/Feedback'
import type { ComputedRef, Ref } from 'vue'
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  captureFeedbackContext,
  feedbackPath,
  feedbackPosition,
  findFeedbackTarget,
} from '@/services/FeedbackContext'
import { useAccountStore } from '@/store/account'
import FeedbackProxy from '@/proxies/FeedbackProxy'
import '@/assets/scss/parts/_feedback.scss'

export default defineComponent({
  name: 'FeedbackOverlay',
  setup() {
    const accountStore = useAccountStore()
    const route = useRoute()
    const router = useRouter()
    const proxy = new FeedbackProxy()
    const threads: Ref<FeedbackThread[]> = ref([])
    const panelOpen: Ref<boolean> = ref(false)
    const picking: Ref<boolean> = ref(false)
    const showPins: Ref<boolean> = ref(true)
    const busy: Ref<boolean> = ref(false)
    const loading: Ref<boolean> = ref(false)
    const error: Ref<string> = ref('')
    const notice: Ref<string> = ref('')
    const selectedId: Ref<string | null> = ref(null)
    const draftContext: Ref<FeedbackContext | null> = ref(null)
    const draft: Ref<string> = ref('')
    const replyText: Ref<string> = ref('')
    const statusFilter: Ref<'open' | 'resolved' | 'all'> = ref('open')
    const onlyThisPage: Ref<boolean> = ref(false)
    const pins: Ref<FeedbackPin[]> = ref([])
    const highlight: Ref<FeedbackHighlight | null> = ref(null)
    const commentInput: Ref<HTMLTextAreaElement | null> = ref(null)
    const launcher: Ref<HTMLButtonElement | null> = ref(null)
    const currentName: ComputedRef<string> = computed(
      () =>
        [accountStore.account.firstName, accountStore.account.lastName].filter(Boolean).join(' ') ||
        accountStore.account.email ||
        'Signed-in user',
    )
    const openCount: ComputedRef<number> = computed(
      () => threads.value.filter((thread) => thread.status === 'open').length,
    )
    const selectedThread: ComputedRef<FeedbackThread | null> = computed(
      () => threads.value.find((thread) => thread.id === selectedId.value) || null,
    )
    const canDelete: ComputedRef<boolean> = computed(() => !!selectedThread.value &&
      (selectedThread.value.author.id === accountStore.userId || accountStore.isStaff))
    const filteredThreads: ComputedRef<FeedbackThread[]> = computed(() => {
      void route.fullPath
      return threads.value
        .filter(
          (thread) =>
            (statusFilter.value === 'all' || thread.status === statusFilter.value) &&
            (!onlyThisPage.value || thread.context.path === feedbackPath()),
        )
        .slice()
        .reverse()
    })
    const highlightStyle: ComputedRef<Record<string, string>> = computed((): Record<string, string> =>
      highlight.value
        ? {
            left: `${highlight.value.left}px`,
            top: `${highlight.value.top}px`,
            width: `${highlight.value.width}px`,
            height: `${highlight.value.height}px`,
          }
        : {},
    )
    let mutationVersion = 0
    let frame = 0
    let timer: ReturnType<typeof setInterval> | undefined
    let noticeTimer: ReturnType<typeof setTimeout> | undefined
    let observer: MutationObserver | undefined
    let destroyed = false

    const initials = (name: string): string =>
      name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase()
    const formatTime = (value: string): string =>
      new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(
        new Date(value),
      )
    const notify = (message: string): void => {
      notice.value = message
      clearTimeout(noticeTimer)
      noticeTimer = setTimeout(() => {
        notice.value = ''
      }, 4500)
    }
    const showError = (reason: unknown): void => {
      error.value =
        (reason as { message?: string })?.message || 'Could not save feedback. Your text is still here; please retry.'
    }
    const updatePins = (): void => {
      frame = 0
      pins.value = threads.value
        .filter(
          (thread) =>
            thread.context.path === feedbackPath() && (thread.status === 'open' || selectedId.value === thread.id),
        )
        .flatMap((thread) => {
          const point = feedbackPosition(thread.context)
          return point && point.x >= 0 && point.y >= 0 && point.x <= window.innerWidth && point.y <= window.innerHeight
            ? [{ thread, ...point }]
            : []
        })
    }
    const schedulePins = (): void => {
      if (!frame) frame = requestAnimationFrame(updatePins)
    }
    const refresh = (): Promise<void> => {
      if (loading.value || !accountStore.account.id) return Promise.resolve()
      loading.value = true
      const version = mutationVersion
      return proxy
        .list()
        .then((response) => {
          if (!destroyed && version === mutationVersion) {
            threads.value = response.data
            error.value = ''
            schedulePins()
          }
        })
        .catch(showError)
        .finally(() => {
          loading.value = false
        })
    }
    const stopPicking = (): void => {
      picking.value = false
      highlight.value = null
      document.getElementById('app')?.classList.remove('feedback-targeting')
    }
    const cancelPicking = (): void => {
      stopPicking()
      panelOpen.value = true
      launcher.value?.focus()
    }
    const startPicking = (): void => {
      if (busy.value) return
      error.value = ''
      panelOpen.value = false
      selectedId.value = null
      picking.value = true
      showPins.value = true
      document.getElementById('app')?.classList.add('feedback-targeting')
    }
    const closePanel = (): void => {
      panelOpen.value = false
      stopPicking()
      launcher.value?.focus()
    }
    const togglePanel = (): void => {
      if (panelOpen.value || picking.value) {
        closePanel()
      } else {
        panelOpen.value = true
        void refresh()
      }
    }
    const togglePins = (): void => {
      showPins.value = !showPins.value
    }
    const discardDraft = (): void => {
      draftContext.value = null
      draft.value = ''
      error.value = ''
    }
    const backToList = (): void => {
      selectedId.value = null
      replyText.value = ''
      error.value = ''
      schedulePins()
    }
    const selectThread = (thread: FeedbackThread): void => {
      stopPicking()
      selectedId.value = thread.id
      panelOpen.value = true
      error.value = ''
      replyText.value = ''
      schedulePins()
    }
    const upsert = (thread: FeedbackThread): void => {
      mutationVersion++
      threads.value = [...threads.value.filter((item) => item.id !== thread.id), thread].sort(
        (a, b) => a.number - b.number,
      )
      schedulePins()
    }
    const postComment = (): Promise<void> => {
      if (busy.value || !draft.value.trim() || !draftContext.value) return Promise.resolve()
      busy.value = true
      error.value = ''
      return proxy
        .add(draft.value.trim(), draftContext.value)
        .then((response) => {
          upsert(response.data)
          draftContext.value = null
          draft.value = ''
          selectThread(response.data)
          notify('Comment saved')
        })
        .catch(showError)
        .finally(() => {
          busy.value = false
        })
    }
    const postReply = (): Promise<void> => {
      if (busy.value || !selectedThread.value || !replyText.value.trim()) return Promise.resolve()
      busy.value = true
      error.value = ''
      return proxy
        .reply(selectedThread.value.id, replyText.value.trim())
        .then((response) => {
          upsert(response.data)
          replyText.value = ''
          notify('Reply saved')
        })
        .catch(showError)
        .finally(() => {
          busy.value = false
        })
    }
    const toggleResolved = (): Promise<void> => {
      if (busy.value || !selectedThread.value) return Promise.resolve()
      busy.value = true
      error.value = ''
      return proxy
        .setStatus(selectedThread.value.id, selectedThread.value.status === 'open' ? 'resolved' : 'open')
        .then((response) => {
          upsert(response.data)
          notify(response.data.status === 'resolved' ? 'Comment resolved' : 'Comment reopened')
        })
        .catch(showError)
        .finally(() => {
          busy.value = false
        })
    }
    const goToLocation = (): Promise<void> => {
      const context = selectedThread.value?.context
      if (!context) return Promise.resolve()
      return router
        .push(context.path)
        .then(() => nextTick())
        .then(() => {
          const target = findFeedbackTarget(context)
          if (target) {
            target.scrollIntoView({ block: 'center', inline: 'center', behavior: 'instant' })
            schedulePins()
          } else
            notify(
              'This element is not visible now. Reopen the original tab or view; the captured context is kept below.',
            )
        })
        .catch(showError)
    }
    // Pages load their data after navigation, so wait a few seconds for the target.
    const revealWhenReady = (context: FeedbackContext, attempts = 32): Promise<void> => {
      const target = findFeedbackTarget(context)
      if (target) {
        target.scrollIntoView({ block: 'center', inline: 'center', behavior: 'instant' })
        schedulePins()
        return Promise.resolve()
      }
      if (attempts <= 0 || destroyed) {
        notify('This element is not visible now. Reopen the original tab or view; the captured context is kept below.')
        return Promise.resolve()
      }
      return new Promise<void>((resolve) => setTimeout(resolve, 250)).then(() => revealWhenReady(context, attempts - 1))
    }
    // A link from Stidner Hub (?feedback=<id>) opens that comment and shows where it is.
    // The parameter is removed first so the page path matches the comment's own path.
    const openFromLink = (): Promise<void> => {
      const id = route.query.feedback
      if (typeof id !== 'string' || !/^[1-9]\d*$/.test(id)) return Promise.resolve()
      const query = { ...route.query }
      delete query.feedback
      return router
        .replace({ path: route.path, query, hash: route.hash })
        .then(() => proxy.list())
        .then((response) => {
          if (destroyed) return
          mutationVersion++
          threads.value = response.data
          const thread = threads.value.find((item) => item.id === id)
          if (!thread) {
            panelOpen.value = true
            notify(`Comment #${id} was not found. It may have been deleted.`)
            return
          }
          if (thread.status === 'resolved') statusFilter.value = 'all'
          selectThread(thread)
          return revealWhenReady(thread.context)
        })
        .catch(showError)
    }
    const deleteComment = (): Promise<void> => {
      if (busy.value || !selectedThread.value || !canDelete.value) return Promise.resolve()
      const thread = selectedThread.value
      if (!window.confirm(`Delete comment #${thread.number} and all its replies? This cannot be undone.`)) return Promise.resolve()
      busy.value = true
      error.value = ''
      return proxy.remove(thread.id)
        .then(() => {
          mutationVersion++
          threads.value = threads.value.filter((item) => item.id !== thread.id)
          backToList()
          notify('Comment deleted')
        })
        .catch(() => { error.value = 'Could not delete the comment. Please retry.' })
        .finally(() => { busy.value = false })
    }
    const isOwnElement = (target: EventTarget | null): boolean =>
      target instanceof Element && !!target.closest('.feedback')
    const pointAt = (element: Element, x: number, y: number): void => {
      draftContext.value = captureFeedbackContext(element, x, y)
      stopPicking()
      panelOpen.value = true
      void nextTick(() => commentInput.value?.focus())
    }
    const onPageClick = (event: MouseEvent): void => {
      if (!picking.value || isOwnElement(event.target) || !(event.target instanceof Element)) return
      event.preventDefault()
      event.stopImmediatePropagation()
      pointAt(event.target, event.clientX, event.clientY)
    }
    const blockPointer = (event: Event): void => {
      if (picking.value && !isOwnElement(event.target)) {
        event.preventDefault()
        event.stopImmediatePropagation()
      }
    }
    const onMouseMove = (event: MouseEvent): void => {
      if (!picking.value || isOwnElement(event.target) || !(event.target instanceof Element)) {
        highlight.value = null
        return
      }
      const rect = event.target.getBoundingClientRect()
      highlight.value = { left: rect.left, top: rect.top, width: rect.width, height: rect.height }
    }
    const onKey = (event: KeyboardEvent): void => {
      const element = event.target instanceof Element ? event.target : null
      if (event.key === 'Escape') {
        if (picking.value) {
          event.preventDefault()
          cancelPicking()
        } else if (panelOpen.value) {
          event.preventDefault()
          closePanel()
        }
        return
      }
      if (picking.value && (event.key === 'Enter' || event.key === ' ') && element && !isOwnElement(element)) {
        event.preventDefault()
        event.stopImmediatePropagation()
        const rect = element.getBoundingClientRect()
        pointAt(element, rect.left + rect.width / 2, rect.top + rect.height / 2)
        return
      }
      if (
        element?.closest('input, textarea, select, [contenteditable="true"]') ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      )
        return
      if (event.key.toLowerCase() === 'c') {
        event.preventDefault()
        if (picking.value) cancelPicking()
        else startPicking()
      }
    }
    watch(() => route.fullPath, schedulePins)
    watch(() => route.query.feedback, () => void openFromLink(), { immediate: true })
    onMounted(() => {
      void refresh()
      document.addEventListener('click', onPageClick, true)
      for (const event of ['pointerdown', 'pointerup', 'mousedown', 'mouseup'])
        document.addEventListener(event, blockPointer, true)
      document.addEventListener('mousemove', onMouseMove, true)
      document.addEventListener('keydown', onKey, true)
      document.addEventListener('scroll', schedulePins, true)
      window.addEventListener('resize', schedulePins)
      observer = new MutationObserver((records) => {
        if (
          records.some(
            (record) =>
              !(record.target instanceof Element ? record.target : record.target.parentElement)?.closest('.feedback'),
          )
        )
          schedulePins()
      })
      const root = document.getElementById('app')
      if (root) observer.observe(root, { childList: true, subtree: true, attributes: true, characterData: true })
      timer = setInterval(() => {
        if (panelOpen.value && !document.hidden && !busy.value) void refresh()
      }, 15000)
    })
    onBeforeUnmount(() => {
      destroyed = true
      stopPicking()
      clearInterval(timer)
      clearTimeout(noticeTimer)
      cancelAnimationFrame(frame)
      observer?.disconnect()
      document.removeEventListener('click', onPageClick, true)
      for (const event of ['pointerdown', 'pointerup', 'mousedown', 'mouseup'])
        document.removeEventListener(event, blockPointer, true)
      document.removeEventListener('mousemove', onMouseMove, true)
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('scroll', schedulePins, true)
      window.removeEventListener('resize', schedulePins)
    })
    return {
      threads,
      panelOpen,
      picking,
      showPins,
      busy,
      loading,
      error,
      notice,
      selectedId,
      draftContext,
      draft,
      replyText,
      statusFilter,
      onlyThisPage,
      pins,
      highlight,
      highlightStyle,
      commentInput,
      launcher,
      currentName,
      openCount,
      selectedThread,
      filteredThreads,
      initials,
      formatTime,
      refresh,
      cancelPicking,
      startPicking,
      closePanel,
      togglePanel,
      togglePins,
      discardDraft,
      backToList,
      selectThread,
      postComment,
      postReply,
      toggleResolved,
      goToLocation,
      deleteComment,
      canDelete,
    }
  },
})
</script>
